import { useState, useCallback, useEffect } from "react";
import { dashboardApi } from "@/apis/dashboard";

export interface StatisticsData {
  // FAQ는 기간과 무관한 현재 상태만
  faqStats: {
    totalCount: number;
    activeCount: number;
    categoryDistribution: Record<string, number>;
    completenessRate: number;
  };
  // 트라이얼 통계 (직접 접근)
  trialStats: {
    thisMonthCount: number;
    totalCount: number;
    growthRate: number;
    conversionRate: number;
    pendingCount: number;
    approvedCount: number;
    completedCount: number;
  };
  // QNA 통계 (직접 접근)
  qnaStats: {
    thisMonthCount: number;
    totalCount: number;
    growthRate: number;
    responseRate: number;
    pendingCount: number;
    answeredCount: number;
    averageResponseTime: number;
  };
  // GA4 트래픽 (직접 접근)
  ga4Traffic: {
    totalSessions: number;
    totalUsers: number;
    newUsers: number;
    returningUsers: number;
    avgBounceRate: number;
    avgSessionDuration: number;
    growthRate: number;
    hasConnection: boolean;
    topTrafficSources: Array<{
      source: string;
      medium: string;
      sessions: number;
      users: number;
    }>;
    dailyStats?: Array<{
      date: string;
      sessions: number;
      users: number;
      bounceRate: number;
    }>;
  };
  // 메타 정보
  period: string;
  startDate: string;
  endDate: string;
  overallScore: number;
  overallGrowthRate: number;
  lastUpdated: string;
}

interface UseStatisticsProps {
  comId: string;
}

/**
 * 통계분석 데이터를 관리하는 커스텀 훅
 * @description 도장별 트라이얼, FAQ, QNA, GA4 통계를 조회하고 관리합니다.
 * @param comId 도장 ID
 * @returns 통계 데이터, 로딩 상태, 에러 상태 및 새로고침 함수
 */
export default function useStatistics({ comId }: UseStatisticsProps) {
  const [stats, setStats] = useState<StatisticsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<"7days" | "30days" | "90days">("30days");

  /**
   * 통계 데이터를 조회합니다.
   */
  const fetchStatistics = useCallback(
    async (selectedPeriod?: "7days" | "30days" | "90days") => {
      if (!comId || comId === "0") {
        console.log("[STATISTICS_API]", {
          action: "fetchStats",
          comId,
          message: "Invalid comId, skipping API call",
        });
        return;
      }

      const currentPeriod = selectedPeriod || period;
      setIsLoading(true);
      setError(null);

      try {
        console.log("[STATISTICS_API]", {
          action: "fetchStats",
          comId,
          period: currentPeriod,
        });

        // ✅ Dashboard와 동일한 API 사용
        const response = await dashboardApi.getCompanyStats(
          parseInt(comId),
          currentPeriod
        );

        console.log("[STATISTICS_API]", {
          action: "fetchStats",
          comId,
          period: currentPeriod,
          success: true,
          response,
        });

        // ✅ 새로운 응답 구조에 맞게 데이터 변환
        const statisticsData: StatisticsData = {
          faqStats: {
            totalCount: response.faqStats?.totalCount || 0,
            activeCount: response.faqStats?.activeCount || 0,
            categoryDistribution: response.faqStats?.categoryDistribution || {},
            completenessRate: response.faqStats?.completenessRate || 0,
          },
          trialStats: {
            thisMonthCount:
              response.periodStats?.trialStats?.thisMonthCount || 0,
            totalCount: response.periodStats?.trialStats?.totalCount || 0,
            growthRate: response.periodStats?.trialStats?.growthRate || 0,
            conversionRate:
              response.periodStats?.trialStats?.conversionRate || 0,
            pendingCount: response.periodStats?.trialStats?.pendingCount || 0,
            approvedCount: response.periodStats?.trialStats?.approvedCount || 0,
            completedCount:
              response.periodStats?.trialStats?.completedCount || 0,
          },
          qnaStats: {
            thisMonthCount: response.periodStats?.qnaStats?.thisMonthCount || 0,
            totalCount: response.periodStats?.qnaStats?.totalCount || 0,
            growthRate: response.periodStats?.qnaStats?.growthRate || 0,
            responseRate: response.periodStats?.qnaStats?.responseRate || 0,
            pendingCount: response.periodStats?.qnaStats?.pendingCount || 0,
            answeredCount: response.periodStats?.qnaStats?.answeredCount || 0,
            averageResponseTime:
              response.periodStats?.qnaStats?.averageResponseTime || 0,
          },
          ga4Traffic: {
            totalSessions: response.periodStats?.ga4Traffic?.totalSessions || 0,
            totalUsers: response.periodStats?.ga4Traffic?.totalUsers || 0,
            newUsers: response.periodStats?.ga4Traffic?.newUsers || 0,
            returningUsers:
              response.periodStats?.ga4Traffic?.returningUsers || 0,
            avgBounceRate: response.periodStats?.ga4Traffic?.avgBounceRate || 0,
            avgSessionDuration:
              response.periodStats?.ga4Traffic?.avgSessionDuration || 0,
            growthRate: response.periodStats?.ga4Traffic?.growthRate || 0,
            hasConnection:
              response.periodStats?.ga4Traffic?.hasConnection || false,
            topTrafficSources:
              response.periodStats?.ga4Traffic?.topTrafficSources || [],
            dailyStats: response.periodStats?.ga4Traffic?.dailyStats || [],
          },
          period: response.periodStats?.period || currentPeriod,
          startDate: response.periodStats?.startDate || "",
          endDate: response.periodStats?.endDate || "",
          overallScore: response.periodStats?.overallScore || 0,
          overallGrowthRate: response.periodStats?.overallGrowthRate || 0,
          lastUpdated: response.lastUpdated || new Date().toISOString(),
        };

        setStats(statisticsData);
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "통계 데이터를 불러오는데 실패했습니다.";
        const errorStack = error instanceof Error ? error.stack : undefined;

        console.error("[STATISTICS_API]", {
          action: "fetchStats",
          comId,
          period: currentPeriod,
          error: errorMessage,
          stack: errorStack,
        });
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [comId, period]
  );

  /**
   * 기간을 변경하고 데이터를 새로 조회합니다.
   */
  const changePeriod = useCallback(
    (newPeriod: "7days" | "30days" | "90days") => {
      setPeriod(newPeriod);
      fetchStatistics(newPeriod);
    },
    [fetchStatistics]
  );

  /**
   * 통계 데이터를 새로고침합니다.
   */
  const refreshStats = useCallback(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // 컴포넌트 마운트 시 데이터 조회
  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  return {
    stats,
    isLoading,
    error,
    period,
    refreshStats,
    changePeriod,
  };
}
